import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { scaledCoordinate } from '../drawing.helper'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'

export type FlatTopBottomDrawingState = {
    p1?: Point,
    p2?: Point,
    p3?: Point,
    p4?: Point
}

export type FlatTopBottomDrawingSettings = {
    fillColor: ColorValue,
    lineColor: ColorValue,
    lineWidth: number,
    labelColor: ColorValue
}

class FlatTopBottomDrawingTool extends DrawingBase<any, FlatTopBottomDrawingSettings, FlatTopBottomDrawingState> {

    slope: number = 0
    intercept = 0
    scaledCoordinates = {
        p1x: 0,
        p2x: 0,
        p1y: 0,
        p2y: 0,
        p3x: 0,
        p3y: 0,
        p4x: 0,
        p4y: 0

    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        this.calculateLineAttributes()
        let { p1x, p2x, p1y, p2y, p3x, p3y, p4x, p4y } = this.scaledCoordinates
        if (!p1x || !p2x || !p1y || !p2y) return

        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.strokeStyle = this._settings.lineColor.color
        ctx.lineWidth = this._settings.lineWidth * window.devicePixelRatio

        ctx.beginPath()

        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p2x, p2y)
        ctx.stroke()

        if (!p3x || !p4x || !p3y || !p4y) return

        ctx.moveTo(p4x, p4y)
        ctx.lineTo(p3x, p3y)
        ctx.stroke()
        ctx.closePath()


        ctx.beginPath()
        ctx.globalAlpha = 0
        ctx.moveTo(p1x, p1y)
        ctx.lineTo(p2x, p2y)
        ctx.lineTo(p4x, p4y)
        ctx.lineTo(p3x, p3y)
        ctx.lineTo(p1x, p1y)
        ctx.closePath()
        ctx.globalAlpha = this._settings.fillColor.opacity
        ctx.fillStyle = this._settings.fillColor.color
        ctx.fill()
    };

    calculateLineAttributes() {
        const { p1, p2, p3, p4 } = this._state
        if (!p1 || !p2) return

        let p1x = this.timeToCoordinate(p1.x)
        let p2x = this.timeToCoordinate(p2.x)
        let p1y = this.priceToCoordinate(p1.y)
        let p2y = this.priceToCoordinate(p2.y)
        if (p1x && p2x && p1y && p2y) {
            this.scaledCoordinates.p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
            this.slope = (p2y - p1y) / (p2x - p1x)
            this.intercept = p1y - this.slope * p1x
        }

        if (!p3 || !p4) return

        let p3x = this.timeToCoordinate(p3.x)
        let p4x = this.timeToCoordinate(p4.x)
        let p3y = this.priceToCoordinate(p3.y)
        let p4y = this.priceToCoordinate(p4.y)

        if (p3x && p4x && p3y && p4y) {
            this.scaledCoordinates.p3x = scaledCoordinate(p3x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p3y = scaledCoordinate(p3y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates.p4x = scaledCoordinate(p4x, this.horizontalPixelRatio) as Coordinate
            this.scaledCoordinates.p4y = scaledCoordinate(p4y, this.verticalPixelRatio) as Coordinate
        }
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {

        const { p1, p2 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
        } else if (this.lastAddedPoint.id == 'p2') {
            point.id = 'p4'
            point.x = p2!.x
            point.y = price
            this._state.p4 = point

            this._state.p3 = {
                id: 'p3',
                x: p1!.x,
                y: point.y
            }
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }
    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2, p3, p4 } = this._state
        if (p1) {
            handles.push(p1)
        }


        p2 && handles.push(p2)
        p3 && handles.push(p3)
        p4 && handles.push(p4)

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const { p1x, p1y, p2x, p2y, p3x, p3y } = this.scaledCoordinates
        const minX = Math.min(p1x, p2x), maxX = Math.max(p1x, p2x)
        if (scaledX < minX || scaledX > maxX) return null
        const line1Y = this.slope * scaledX + this.intercept
        const line2Y = p3y

        const minY = Math.min(line1Y, line2Y), maxY = Math.max(line1Y, line2Y)

        if (scaledY < minY || scaledY > maxY) return null

        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
                    break
                case 'p3':
                    this._state.p3 = h
                    break
                case 'p4':
                    this._state.p4 = h
                    break

            }
        })
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1, p2, p3, p4 } = this._state
        if (!p1 || !p2 || !p3 || !p4) return
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = price
                p3.x = time
                break
            case 'p2':
                p2.x = time
                p2.y = price
                p4.x = time
                break
            case 'p3':
                p3.x = time
                p3.y = price
                p1.x = time
                p4.y = p3.y
                break
            case 'p4':
                p4.y = price
                p4.x = time
                p2.x = time
                p3.y = p4.y
                break
        }
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {

        const { p1, p2, p3, p4 } = this._state
        if (!p1 || !p2) return []

        if (!p3 || !p4) {
            const min = Math.min(p1.y, p2.y)
            const max = Math.max(p1.y, p2.y)

            return [{
                id: 'price',
                p1: min,
                p2: max,
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }]
        } else {

            return [{
                id: 'start',
                p1: Math.min(p1.y, p2.y),
                p2: Math.max(p1.y, p2.y),
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }, {
                id: 'end',
                p1: Math.min(p1.y, p3.y),
                p2: Math.max(p1.y, p3.y),
                fill: true,
                fillColor: this._settings.fillColor.color,
                labelColors: {
                    p1: this._settings.labelColor.color,
                    p2: this._settings.labelColor.color
                }
            }]
        }

    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)

        if (!p1x || !p2x) return []
        let min: Time = p1.x, max: Time = p2.x
        if (p1x > p2x) {
            min = p2.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }


    getAvailableSettings = (): DrawingSettingGroup<FlatTopBottomDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#ff9900',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#2962ff',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: { color: '#ff9900', opacity: 1 },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): FlatTopBottomDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof FlatTopBottomDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name).filter(v => v !== 'labelColor')]
            }
        })

        return miniSettings
    }

    getDrawingType(): DrawingTypes {
        return 'flatTopBottom'
    }
}

export default FlatTopBottomDrawingTool